<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store.js';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const emit = defineEmits(['add', 'delete', 'refresh']);

const { t } = useI18n();

const websites = useMapGetter('metaAgent/getWebsites');

const url = ref('');

const add = () => {
  if (!url.value) return;
  emit('add', url.value);
  url.value = '';
};

const remove = website => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('META_AGENT.KNOWLEDGE.WEBSITES.DELETE_CONFIRM')))
    return;
  emit('delete', website.id);
};

// Meta reports free-form casing, so the label lookup is normalised and falls
// back to the raw status rather than rendering a missing translation key.
const crawlSummary = website => {
  const status = (website.crawl_status || '').toUpperCase();
  const label = t(`META_AGENT.KNOWLEDGE.WEBSITES.STATUS.${status}`, status);
  // Passing the number (not a named param) so vue-i18n picks the plural form;
  // interpolation alone rendered "1 páginas".
  const pages = t(
    'META_AGENT.KNOWLEDGE.WEBSITES.PAGES_CRAWLED',
    website.pages_crawled || 0
  );
  return `${label} · ${pages}`;
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-start justify-between gap-4">
      <span class="text-sm text-n-slate-11">
        {{ t('META_AGENT.KNOWLEDGE.WEBSITES.DESCRIPTION') }}
      </span>
      <!-- Meta crawls in the background and never notifies, so the status is
           only as fresh as the last read. -->
      <Button
        v-if="websites.length"
        :label="t('META_AGENT.KNOWLEDGE.WEBSITES.REFRESH')"
        size="sm"
        variant="faded"
        color="slate"
        icon="i-lucide-refresh-cw"
        @click="emit('refresh')"
      />
    </div>

    <div class="flex items-center gap-2">
      <Input
        v-model="url"
        :placeholder="t('META_AGENT.KNOWLEDGE.WEBSITES.PLACEHOLDER')"
        class="flex-1"
        @keyup.enter="add"
      />
      <Button
        :label="t('META_AGENT.KNOWLEDGE.WEBSITES.ADD')"
        size="sm"
        variant="faded"
        color="slate"
        @click="add"
      />
    </div>

    <div
      v-for="website in websites"
      :key="website.id"
      class="flex items-center justify-between gap-4 p-4 border rounded-xl bg-n-solid-1 border-n-weak"
    >
      <div class="flex flex-col gap-1 min-w-0">
        <span class="text-sm truncate text-n-slate-12">{{ website.url }}</span>
        <span class="text-xs text-n-slate-11">
          {{ crawlSummary(website) }}
        </span>
      </div>
      <Button
        :label="t('META_AGENT.KNOWLEDGE.WEBSITES.REMOVE')"
        size="xs"
        variant="ghost"
        color="ruby"
        @click="remove(website)"
      />
    </div>

    <span v-if="!websites.length" class="text-sm text-n-slate-10">
      {{ t('META_AGENT.KNOWLEDGE.WEBSITES.EMPTY') }}
    </span>
  </div>
</template>
